/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { getRichTextHtml, IGridSettings, IImageData, Image, RichText, RichTextComponent } from '@msdyn365-commerce/core';

import * as React from 'react';

import Badge from 'mfcl/Badge';
import Countdown, { CountdownRenderProps, zeroPad } from 'react-countdown';

interface ISaleData {
    image: IImageData;
    carouselIndex: number;
    title: RichText;
    gridSettings: IGridSettings;
    description: RichText | undefined;
    promoBadge: string | undefined;
    countdown: Date | undefined;
    linkTo: string;
    salesTitle: boolean;
}

export default (props: ISaleData) => {
    const styles = 'd5-mfrm-content-reel__';
    const { image, title, gridSettings, description, promoBadge = '', countdown = '', linkTo, salesTitle } = props;

    const handleAnalytics = () => {
        // @todo Update the analytic setup once requirement is provided
        if (MsDyn365.isBrowser) {
            window.location.href = linkTo;
        }
    };

    const featuredSaleRefSale = React.useRef<any>();
    React.useEffect(() => {
        featuredSaleRefSale.current && featuredSaleRefSale.current.addEventListener('click', handleClick);
    }, []);

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        handleAnalytics();
    };

    const [countdownRunning, setCountdownRunning] = React.useState(!!countdown);
    const renderer = ({ days, hours, minutes, seconds, completed }: CountdownRenderProps) => {
        if (completed) {
            setCountdownRunning(false);
            return <></>;
        } else {
            return (
                <span className={`countdown-${days ? 'lg' : 'sm'}`}>
                    {days ? `${zeroPad(days, 2)}:` : null}
                    {zeroPad(hours, 2)}:{zeroPad(minutes, 2)}:{zeroPad(seconds, 2)}
                </span>
            );
        }
    };
    return (
        <a
            className={`${styles}link`}
            ref={featuredSaleRefSale}
            href={linkTo}
        >
            <div className={`${styles}sale`}>
                <Image {...image} alt={image?.altText} className={`${styles}image`} src={image?.src || ''} gridSettings={gridSettings} />
                <div className={`${styles}text-container`}>
                    {title && <div className={`${styles}title${salesTitle ? ' sales' : ''}`} dangerouslySetInnerHTML={getRichTextHtml(title)}/>}
                    {description && <RichTextComponent className={`${styles}description-container`} text={description} />}
                </div>

                {promoBadge && <Badge className={`${styles}badge-container ${styles}promo`}>{promoBadge}</Badge>}
                {countdown && countdownRunning && (
                    <Badge className={`${styles}badge-container ${styles}countdown`} type='secondary'>
                        <Countdown date={countdown} renderer={renderer} />
                    </Badge>
                )}
            </div>
        </a>
    );
};
